import { useState } from "react";
import type { FoundAddress } from "@shared-core/data/api-types";
import { useObjectUiAdapter } from "@shared-core/data/ObjectUiAdapterContext";
import { extractFieldErrors } from "@shared-core/data/validation";
import type { FieldError } from "@shared-core/domain/object-ui/contract";
import type { FieldDescriptor } from "@shared-core/domain/object-ui/widgets";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { Textarea } from "../../primitives/textarea";
import { fieldId, FieldFrame, invalidProps } from "./CommandFields";

/** The address widget's value: what an address request's fields hold. */
export interface AddressValue {
  lines: string[];
  town: string | null;
  postcode: string;
  country_code: string;
}

function asAddress(value: unknown): AddressValue {
  const v = (value ?? {}) as Partial<AddressValue>;
  return {
    lines: Array.isArray(v.lines) ? v.lines : [],
    town: v.town ?? null,
    postcode: v.postcode ?? "",
    country_code: v.country_code ?? "GB",
  };
}

type Lookup =
  | { kind: "idle" }
  | { kind: "looking" }
  | { kind: "found"; premises: FoundAddress[] }
  | { kind: "none" }
  | { kind: "unavailable" }
  | { kind: "refused"; message: string };

/**
 * Address with postcode lookup (brief §9.3): *Find* lists the premises at a
 * postcode and fills the address from the one chosen; the address can always
 * be typed instead.
 */
export function AddressField({
  field,
  value,
  lookupHref,
  errors,
  disabled,
  onChange,
}: {
  field: FieldDescriptor;
  value: unknown;
  /** The dry run's postcode lookup for this record; no Find without it. */
  lookupHref: string | null;
  errors: FieldError[];
  disabled: boolean;
  onChange: (value: AddressValue, options?: { debounce?: boolean }) => void;
}) {
  const adapter = useObjectUiAdapter();
  const id = fieldId(field.path);
  const address = asAddress(value);
  const [lookup, setLookup] = useState<Lookup>({ kind: "idle" });

  const find = async () => {
    if (!lookupHref || !address.postcode.trim()) return;
    setLookup({ kind: "looking" });
    try {
      const premises = await adapter.lookupPostcode(
        lookupHref,
        address.postcode,
      );
      setLookup(
        premises.length ? { kind: "found", premises } : { kind: "none" },
      );
    } catch (err) {
      // A malformed postcode is a field error; anything else (503: no lookup
      // provider set up) means the address is typed in.
      const refused = extractFieldErrors(err)[0];
      setLookup(
        refused
          ? { kind: "refused", message: refused.message }
          : { kind: "unavailable" },
      );
    }
  };

  const lookupMessage =
    lookup.kind === "none"
      ? "Nothing found at that postcode: type the address."
      : lookup.kind === "unavailable"
        ? "Postcode lookup isn't available: type the address."
        : lookup.kind === "refused"
          ? lookup.message
          : null;

  return (
    <FieldFrame
      id={id}
      label={field.label}
      required={field.required}
      errors={errors}
      asGroup
    >
      <div className="space-y-2" role="group" aria-labelledby={`${id}-label`}>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor={`${id}-postcode`} className="font-normal">
              Postcode
            </Label>
            <Input
              id={`${id}-postcode`}
              className="max-w-40 uppercase"
              value={address.postcode}
              disabled={disabled}
              {...invalidProps(id, errors)}
              onChange={(e) =>
                onChange(
                  { ...address, postcode: e.target.value },
                  { debounce: true },
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void find();
                }
              }}
            />
          </div>
          {lookupHref && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={
                disabled ||
                lookup.kind === "looking" ||
                !address.postcode.trim()
              }
              onClick={() => void find()}
            >
              {lookup.kind === "looking" ? "Finding…" : "Find"}
            </Button>
          )}
        </div>
        {lookupMessage && (
          <p role="status" className="text-sm text-muted-foreground">
            {lookupMessage}
          </p>
        )}
        {lookup.kind === "found" && (
          <Select
            onValueChange={(chosen) => {
              const premises = lookup.premises[Number(chosen)];
              if (!premises) return;
              onChange({
                lines: [
                  ...(premises.organisation ? [premises.organisation] : []),
                  ...premises.lines,
                ],
                town: premises.town,
                postcode: premises.postcode,
                country_code: "GB",
              });
            }}
          >
            <SelectTrigger aria-label="Premises" className="w-full max-w-md">
              <SelectValue placeholder="Choose the premises…" />
            </SelectTrigger>
            <SelectContent>
              {lookup.premises.map((p, n) => (
                <SelectItem key={p.label} value={String(n)}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="space-y-1">
          <Label htmlFor={`${id}-lines`} className="font-normal">
            Address lines
          </Label>
          <Textarea
            id={`${id}-lines`}
            value={address.lines.join("\n")}
            disabled={disabled}
            placeholder="One line per row"
            onChange={(e) =>
              onChange(
                { ...address, lines: e.target.value.split("\n") },
                { debounce: true },
              )
            }
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${id}-town`} className="font-normal">
            Town
          </Label>
          <Input
            id={`${id}-town`}
            className="max-w-sm"
            value={address.town ?? ""}
            disabled={disabled}
            onChange={(e) =>
              onChange(
                { ...address, town: e.target.value || null },
                { debounce: true },
              )
            }
          />
        </div>
      </div>
    </FieldFrame>
  );
}
